import React, { useEffect, useState, useCallback, useMemo } from 'react';
import MetaTags from 'react-meta-tags';
import { useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { RMVendorTable } from './Components/RMVendorTable ';
import { AddButton } from './Components/AddButton';
import { AddEditRMVendorModal } from './Modals/AddEditRMVendorModal';
import { DeleteRMVendorConfirmationModal } from './Modals/DeleteRMVendorConfirmationModal';
import {
  createRMVendor,
  updateRMVendor,
  deleteRMVendor,
  getAllRMVendors
} from './Helpers/Api';
import { getValidationSchema, getInitialValues } from './Helpers/Config';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import './styles/styles.css';
import debounce from 'lodash/debounce';
import { getPropertyList } from '../FacilityInventory/Helpers/Api';

const RMVendorManagement = () => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState('');
  const [rmVendors, setRMVendors] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [properties, setProperties] = useState([]);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'

  // Memoized token decoding
  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch {
      return null;
    }
  }, []);

  // Fetch properties with memoized callback
  const fetchProperties = useCallback(async () => {
    const user = getUserFromToken();
    if (!user) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await getPropertyList(user?.role || '', user?.id || '');
      setProperties(result?.propertyList || []);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to fetch properties.',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, getUserFromToken]);

  const validationSchema = useMemo(() => getValidationSchema(modalMode), [modalMode]);

  const formik = useFormik({
    initialValues: getInitialValues(modalMode, selectedSupplier),
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        const user = getUserFromToken();

        const processedValues = {
          ...values,
          items: (values.items || []).map(item => ({
            ...item,
            images: (item.images || [])
              .filter(img => img.url)
              .map(img => ({
                ...img,
                uploadedBy: {
                  id: user?.id || '',
                  role: user?.role || '',
                },
              })),
          })),
        };

        let result;
        if (modalMode === 'add') {
          result = await createRMVendor(processedValues);
        } else if (modalMode === 'edit') {
          result = await updateRMVendor(selectedSupplier?._id, processedValues);
        }

        if (!result.success) throw new Error(result.message || `Failed to ${modalMode} vendor`);

        await Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Vendor ${modalMode === 'add' ? 'created' : 'updated'} successfully!`,
        });

        await getRMVendorsData();
        resetForm();
        closeModal();
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || `Failed to ${modalMode} vendor.`,
        });
      } finally {
        setSubmitting(false);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  });

  const getRMVendorsData = useCallback(debounce(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await getAllRMVendors();
      setRMVendors(response?.data || []);
    } catch (error) {
      console.error('Error fetching RM vendors:', error);
      setRMVendors([]);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, 300), [dispatch]);

  const openAddModal = useCallback(() => {
    setModalMode('add');
    setSelectedSupplier(null);
    setModal('add');
  }, []);

  const openEditModal = useCallback((supplier) => {
    setModalMode('edit');
    setSelectedSupplier(supplier);
    setModal('edit');
  }, []);

  const openDeleteModal = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setModal('delete');
  }, []);

  const closeModal = useCallback(() => {
    setModal('');
    setSelectedSupplier(null);
    setModalMode('add');
    formik.resetForm();
  }, [formik]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      if (!selectedSupplier?._id) throw new Error('No supplier selected');

      dispatch({ type: 'SET_LOADING', payload: true });
      await deleteRMVendor(selectedSupplier._id);

      await Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Vendor deleted successfully'
      });

      await getRMVendorsData();
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to delete vendor'
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch, selectedSupplier, getRMVendorsData, closeModal]);

  useEffect(() => {
    const newValues = getInitialValues(modalMode, selectedSupplier);
    formik.setValues(newValues);
  }, [modalMode, selectedSupplier]);

  useEffect(() => {
    fetchProperties();
    getRMVendorsData();
  }, [fetchProperties, getRMVendorsData]);

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Rentdigicare | RM Vendors</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title="Management"
            breadcrumbItem="RM Vendors"
          />

          <AddButton
            onAddClick={openAddModal}
          />

          <RMVendorTable
            vendors={rmVendors}
            onEdit={openEditModal}
            onDelete={openDeleteModal}
          />

          <AddEditRMVendorModal
            mode={modalMode}
            open={['add', 'view', 'edit'].includes(modal)}
            onClose={closeModal}
            formik={formik}
            properties={properties}
          />

          <DeleteRMVendorConfirmationModal
            open={modal === 'delete'}
            onClose={closeModal}
            onDelete={handleDeleteConfirm}
            vendor={selectedSupplier}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export { RMVendorManagement };